import { ref, Ref } from 'vue'

// Accept external refs as parameters
export function useBookingUI(options?: {
  externalReservationDetailsRef?: Ref<HTMLElement | null>;
  externalTermsCheckboxRef?: Ref<HTMLInputElement | null>;
}) {
  // UI-related state
  const isDateSectionCollapsed = ref(false)
  const isTimeSectionCollapsed = ref(false)
  const isTermsExpanded = ref(false)
  const showTermsError = ref(false)
  const isFormValid = ref(false)
  
  // References to DOM elements
  const guestInfoForm = ref<any>(null)
  
  // Use external refs if provided, otherwise create new ones
  const reservationDetailsRef = options?.externalReservationDetailsRef || ref<HTMLElement | null>(null)
  const termsCheckboxRef = options?.externalTermsCheckboxRef || ref<HTMLInputElement | null>(null)
  
  // Toggle terms expansion
  const toggleTerms = () => {
    isTermsExpanded.value = !isTermsExpanded.value
  }
  
  // Animation controls for section collapsing/expanding
  const toggleDateSection = (collapsed: boolean | null = null) => {
    // Add slight delay when expanding for a better feel
    if (collapsed === false) {
      // Create a more natural interaction by delaying the expansion
      setTimeout(() => {
        isDateSectionCollapsed.value = false
      }, 50) // Slight delay feels more intentional but fast
    } else if (collapsed !== null) {
      isDateSectionCollapsed.value = collapsed
    } else {
      isDateSectionCollapsed.value = !isDateSectionCollapsed.value
    }
  }

  const toggleTimeSection = (collapsed: boolean | null = null) => {
    // Add slight delay when expanding for a better feel
    if (collapsed === false) {
      // Create a more natural interaction by delaying the expansion
      setTimeout(() => {
        isTimeSectionCollapsed.value = false
      }, 50) // Slight delay feels more intentional but fast
    } else if (collapsed !== null) {
      isTimeSectionCollapsed.value = collapsed
    } else {
      isTimeSectionCollapsed.value = !isTimeSectionCollapsed.value
    }
  }

  // Transition methods for enter/leave animations
  const onTransitionEnter = (el: Element, done: () => void) => {
    const htmlEl = el as HTMLElement
    
    // Start from collapsed state
    htmlEl.style.overflow = 'hidden'
    htmlEl.style.height = '0'
    htmlEl.style.opacity = '0'
    htmlEl.style.transform = 'scale(0.95) translateY(5px)'
    
    // Force reflow
    void htmlEl.offsetHeight
    
    // Add transition - even faster animation (additional 20% faster)
    htmlEl.style.transition = 'height 240ms ease-out, opacity 184ms ease-out, transform 184ms ease-out'
    
    // Set end state
    setTimeout(() => {
      htmlEl.style.height = htmlEl.scrollHeight + 'px'
      htmlEl.style.opacity = '1'
      htmlEl.style.transform = 'scale(1) translateY(0)'
    }, 10)
    
    // Clean up
    const handleTransitionEnd = () => {
      htmlEl.style.removeProperty('overflow')
      htmlEl.style.removeProperty('height')
      htmlEl.style.removeProperty('transition')
      htmlEl.removeEventListener('transitionend', handleTransitionEnd)
      done()
    }
    
    // Use the height transition as our indicator since it's the longest
    htmlEl.addEventListener('transitionend', (e) => {
      if (e.propertyName === 'height') {
        handleTransitionEnd()
      }
    })
  }

  const onTransitionLeave = (el: Element, done: () => void) => {
    const htmlEl = el as HTMLElement
    
    // Set starting state
    htmlEl.style.overflow = 'hidden'
    htmlEl.style.height = htmlEl.scrollHeight + 'px'
    htmlEl.style.opacity = '1'
    htmlEl.style.transform = 'scale(1) translateY(0)'
    
    // Force reflow
    void htmlEl.offsetHeight
    
    // Add transition - even faster collapse animation (additional 20% faster)
    htmlEl.style.transition = 'height 320ms cubic-bezier(0.25, 1, 0.5, 1), opacity 200ms ease-in-out, transform 180ms ease-in-out'
    
    // Set end state
    setTimeout(() => {
      htmlEl.style.height = '0'
      htmlEl.style.opacity = '0'
      htmlEl.style.transform = 'scale(0.95) translateY(5px)'
    }, 10)
    
    // Clean up
    const handleTransitionEnd = () => {
      htmlEl.style.removeProperty('overflow')
      htmlEl.style.removeProperty('height')
      htmlEl.style.removeProperty('transition')
      htmlEl.removeEventListener('transitionend', handleTransitionEnd)
      done()
    }
    
    // Use the height transition as our indicator since it's the longest
    htmlEl.addEventListener('transitionend', (e) => {
      if (e.propertyName === 'height') {
        handleTransitionEnd()
      }
    })
  }
  
  // Handle form validation
  const handleFormValidation = (isValid: boolean, guestInfo: any) => {
    // Update form validity state
    isFormValid.value = isValid
    
    // Double-check we have a complete, valid phone number
    const hasValidMobile = isValid && 
                           guestInfo?.mobile?.number && 
                           guestInfo.mobile.number.trim().length > 0 &&
                           guestInfo.mobile.countryCode
    
    // Only expand terms section if the form is valid, but don't collapse if already expanded
    if (isValid && guestInfo?.name && hasValidMobile) {
      // Store current values to detect if user is still typing
      const initialName = guestInfo?.name;
      const initialMobileNumber = guestInfo?.mobile?.number;
      
      // Always expand terms when form is valid to guide user to next step
      if (!isTermsExpanded.value) {
        isTermsExpanded.value = true
      }

      // Always attempt to scroll after validation is successful to improve UX
      // Delayed scroll to reservation details to give time for terms to expand
      setTimeout(() => {
        try {
          // Verify form is still complete before scrolling (user may have cleared fields)
          const formIsIncomplete = !guestInfo?.name || !guestInfo?.mobile?.number;

          if (formIsIncomplete) {
            return;
          }
          
          // Try the main reference first for optimal scroll positioning
          if (reservationDetailsRef.value && typeof reservationDetailsRef.value.scrollIntoView === 'function') {
            reservationDetailsRef.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
          } else {
            // Fallback: try to scroll to the terms section if main ref unavailable
            const termsSection = document.querySelector('.terms-section')
            if (termsSection && termsSection instanceof HTMLElement) {
              termsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
            } else {
              // Last resort: scroll down to approximate location
              window.scrollBy({ top: 300, behavior: 'smooth' })
            }
          }
        } catch (err) {
          console.error('Error during scroll attempt:', err instanceof Error ? err.message : 'Unknown error')
        }
        
        // Focus on terms checkbox after delay to guide user attention
        setTimeout(() => {
          // Verify form is still complete before focusing (user may have cleared fields)
          const formIsStillIncomplete = !guestInfo?.name || !guestInfo?.mobile?.number;

          if (formIsStillIncomplete) {
            return;
          }

          // Try using the provided ref first for optimal focus
          if (termsCheckboxRef.value) {
            termsCheckboxRef.value.focus()
          } else {
            // If the ref doesn't work, try finding checkbox in the DOM

            // Check if reservation details element is available
            const reservationEl = reservationDetailsRef.value || document.querySelector('.reservation-details')
            if (reservationEl) {
              // Try to find checkbox within reservation details by specific class
              const checkbox = reservationEl.querySelector('input[type="checkbox"].terms-checkbox')
              if (checkbox && checkbox instanceof HTMLInputElement) {
                checkbox.focus()
              } else {
                // Try any checkbox in the reservation details as fallback
                const anyCheckbox = reservationEl.querySelector('input[type="checkbox"]')
                if (anyCheckbox && anyCheckbox instanceof HTMLInputElement) {
                  anyCheckbox.focus()
                } else {
                  console.warn('Could not find any checkbox in the DOM')
                }
              }
            } else {
              console.warn('Reservation details element not found, cannot locate checkbox')
            }
          }
        }, 300)
      }, 350) // Slightly reduced delay for better responsiveness
    }
  }
  
  // Handle special request field focus
  const handleSpecialRequestFocus = () => {
    // If the form fields (name and phone) are valid, expand the terms section for better UX flow

    if (isFormValid.value && !isTermsExpanded.value) {
      isTermsExpanded.value = true

      // Attempt to locate and update checkbox reference for future focus operations
      setTimeout(() => {
        // Look for checkbox using querySelector as backup to ref
        const checkbox = document.querySelector('input[type="checkbox"].terms-checkbox')
        if (checkbox) {
          if (termsCheckboxRef.value !== checkbox) {
            termsCheckboxRef.value = checkbox as HTMLInputElement
          }
        } else {
          console.warn('Could not find terms checkbox in DOM using class')
        }
      }, 100)
    }
  }
  
  return {
    // State
    isDateSectionCollapsed,
    isTimeSectionCollapsed,
    isTermsExpanded,
    showTermsError,
    isFormValid,
    
    // Refs
    guestInfoForm,
    termsCheckboxRef,
    reservationDetailsRef,
    
    // Methods
    toggleTerms,
    toggleDateSection,
    toggleTimeSection,
    onTransitionEnter,
    onTransitionLeave,
    handleFormValidation,
    handleSpecialRequestFocus
  }
} 